import { FunctionComponent } from "react";

import ChevronLeft from "@uui/ChevronLeft";
import ChevronRight from "@uui/ChevronRight";

import {
  IconButtonProps,
  Tab as MuiTab,
  TabProps as MuiTabProps,
} from "@mui/material";
import styled from "@mui/material/styles/styled";

import IconButton from "@rui/IconButton";
import Stack, { Row, StackProps } from "@rui/Stack";

export const Container = styled(Stack)({
  backgroundColor: "#FFF",
  height: "100vh",
  width: "100%",
});

export const ContentContainer = styled(Stack)(({ theme }) => ({
  flexDirection: "column",
  flex: 1,
  [theme.breakpoints.up("md")]: {
    flexDirection: "row",
    overflow: "hidden",
  },
}));

type PanelProps = StackProps & { expanded: boolean };

export const LeftPanel = styled<FunctionComponent<PanelProps>>(
  ({ expanded: _, ...props }) => <Stack {...props} />
)(({ expanded, theme }) => ({
  width: "100%",
  padding: theme.spacing(2, 3, 0),
  height: expanded ? "100%" : "auto",

  [theme.breakpoints.up("md")]: {
    width: expanded ? "100%" : "33%",
    minWidth: "396px",
    height: "100%",
    overflow: "hidden",
  },
}));

export const LeftPanelContentContainer = styled<FunctionComponent<PanelProps>>(
  ({ expanded: _, ...props }) => <Stack {...props} />
)(({ expanded, theme }) => ({
  flex: 1,
  padding: expanded ? theme.spacing(0, 0, 2) : 0,
  position: "relative",

  [theme.breakpoints.up("md")]: {
    padding: theme.spacing(0, 0, 2),
    overflowY: "auto",
  },
}));

export const LeftPanelContent = styled(Stack)(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    display: "none",
  },
}));

export const RightPanel = styled<FunctionComponent<PanelProps>>(
  ({ expanded: _, ...props }) => <Stack {...props} />
)(({ expanded, theme }) => ({
  width: "100%",
  backgroundColor: theme.palette.shades.ghost[300],
  flexGrow: expanded ? 1 : 0,
  height: expanded ? "auto" : "0px",

  [theme.breakpoints.up("md")]: {
    height: "100%",
    maxWidth: expanded ? undefined : "0px",
    overflowX: "hidden",
    borderLeft: `1px solid ${theme.palette.shades.neutral[100]}`,
  },
}));

export const RightPanelContent = styled(Stack)({
  overflowY: "auto",
  position: "relative",
});

export const TabsContainer = styled(Stack)(({ theme }) => ({
  borderBottom: `2px solid ${theme.palette.shades.neutral[100]}`,
  "& .MuiTabs-root": {
    marginBottom: "-2px",
  },
}));

type TabProps = MuiTabProps & { count?: number };
export const Tab = styled(({ count, ...props }: TabProps) => (
  <MuiTab
    icon={
      (count ?? 0) > 0 ? (
        <Stack variant="center" className="tab-count">
          {count}
        </Stack>
      ) : undefined
    }
    iconPosition="end"
    {...props}
  />
))(({ theme }) => ({
  minHeight: "48px",
  minWidth: "110px",
  height: "48px",
  fontSize: "1rem",
  lineHeight: "20px",
  letterSpacing: "-0.02rem",
  fontWeight: 500,
  color: theme.palette.shades.neutral[500],
  borderRadius: "6px",

  "&:hover": {
    color: theme.palette.shades.neutral[700],
  },

  "& .tab-count": {
    borderRadius: "6px",
    color: "#FFF",
    height: "16px",
    minWidth: "16px",
    padding: "0 3px",
    fontSize: "0.75rem",
    backgroundColor: theme.palette.shades.neutral[200],
  },

  "&.Mui-selected": {
    color: theme.palette.shades.primary[500],
    "& .tab-count": {
      backgroundColor: theme.palette.shades.primary[500],
    },
  },
}));

export const RecommendationDetailHeading = styled(Row)(({ theme }) => ({
  padding: theme.spacing(2, 3),
  backgroundColor: "#FFF",
  borderBottom: `1px solid ${theme.palette.shades.neutral[100]}`,
  maxWidth: "100%",
}));

export const RecommendationDetailContent = styled(Stack)(({ theme }) => ({
  padding: theme.spacing(2, 3, 3),
}));

type PaginatorButtonProps = IconButtonProps & { direction: "forward" | "back" };

export const PaginatorButton = styled(
  ({ direction, ...props }: PaginatorButtonProps) => (
    <IconButton {...props}>
      {direction === "forward" ? <ChevronRight /> : <ChevronLeft />}
    </IconButton>
  )
)(({ theme }) => ({
  border: `1px solid ${theme.palette.shades.neutral[100]}`,
  borderRadius: "4px",
  height: "40px",
  width: "40px",
  "& .MuiSvgIcon-root": {
    fontSize: "18px",
  },
}));

export const FilterContainer = styled(Row)(({ theme }) => ({
  padding: theme.spacing(1, 0),
  flexWrap: "wrap",
  rowGap: "4px",
  columnGap: "4px",
  borderBottom: `1px solid ${theme.palette.shades.neutral[50]}`,
  backgroundColor: "#FFF",
}));
